import _ from "lodash";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { ConfirmResult, FutureBookingsConfirmOutcome, Log } from "@ryde/shared";

import { useModalConfirm } from "../../hooks-singletons";

/**
 * Shared approval step for retro-actively updating future bookings.
 *
 * Per the RYDE-7929 PRD the popup is a blocker but must never be noise: it only opens when a trigger
 * field actually changed AND the server reports at least one booking that would move.
 */
export const useFutureBookingsConfirm = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();

  const confirmUpdateFutureBookingsStations = useEvent(
    async ({
      isEdit,
      dirtyFields,
      triggerFields,
      getImpactCount,
    }: {
      isEdit: boolean;
      dirtyFields: Record<string, unknown>;
      triggerFields: readonly string[];
      getImpactCount: () => Promise<number | undefined>;
    }): Promise<FutureBookingsConfirmOutcome> => {
      const hasTriggerChange =
        isEdit &&
        triggerFields.some(field => Boolean(_.get(dirtyFields, field)));
      if (!hasTriggerChange) {
        return FutureBookingsConfirmOutcome.NotApplicable;
      }

      // FutureBookingsImpactApi owns the user-facing reporting for every failure mode (failed
      // SimpleResult, empty response, thrown request) and toasts exactly once, so these branches only
      // log for diagnostics — adding a toast here would stack a second, vaguer message on top.
      let affectedCount: number | undefined;
      try {
        affectedCount = await getImpactCount();
      } catch (error) {
        Log.error("Failed to fetch future bookings impact", error);

        return FutureBookingsConfirmOutcome.PreviewFailed;
      }

      // A missing count is a failed preview, not "nothing to do" — never treat it as approval.
      if (affectedCount === undefined || affectedCount === null) {
        Log.error("Future bookings impact returned no count");

        return FutureBookingsConfirmOutcome.PreviewFailed;
      }

      // PRD: when nothing would change, do not open the popup at all.
      if (affectedCount === 0) {
        return FutureBookingsConfirmOutcome.NoImpact;
      }

      const result = await confirmDialogResult({
        title: t("general.UPDATE_FUTURE_BOOKINGS_STATIONS_TITLE"),
        // i18next picks the _one/_other variant and interpolates {{count}}, so the admin sees how
        // many bookings the answer actually moves before committing to it.
        message: t("general.UPDATE_FUTURE_BOOKINGS_STATIONS_MESSAGE", {
          count: affectedCount,
        }),
        okButtonTitle: t("general.UPDATE_FUTURE_BOOKINGS_STATIONS_CONFIRM"),
        cancelButtonTitle: t("general.UPDATE_FUTURE_BOOKINGS_STATIONS_CANCEL"),
      });

      return result === ConfirmResult.OK
        ? FutureBookingsConfirmOutcome.Approved
        : FutureBookingsConfirmOutcome.Declined;
    },
  );

  return { confirmUpdateFutureBookingsStations };
};
